import * as React from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Key, Lock } from 'lucide-react';

import { kratosApi } from '@/lib/api/kratos';

/**
 * SettingsPage Component
 * 
 * Handles user settings flows using Ory Kratos
 * This page is used for completing password recovery and other settings flows
 * Based on the original Nuxt implementation
 */
export function SettingsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const flowId = searchParams.get('flow');
  
  // UI State - simplified like original
  const [methodSelector, setMethodSelector] = React.useState(true);
  const [registerWebAuthn, setRegisterWebAuthn] = React.useState(false);
  const [registerPassword, setRegisterPassword] = React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  
  // Form Data - matching original exactly
  const [identifier, setIdentifier] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [securityKeyName, setSecurityKeyName] = React.useState('');
  const [csrfToken, setCsrfToken] = React.useState('');
  const [webAuthnLink, setWebAuthnLink] = React.useState('');
  const [passkeyCreateData, setPasskeyCreateData] = React.useState('');
  const [method, setMethod] = React.useState('');

  // Fetch settings flow
  const { data: flow, isLoading, error } = useQuery({
    queryKey: ['settingsFlow', flowId],
    queryFn: async () => {
      if (!flowId) {
        throw new Error('No flow ID provided');
      }
      const response = await kratosApi.getSettingsFlow({ id: flowId });
      return response.data;
    },
    enabled: !!flowId,
    retry: 1,
    staleTime: 0,
  });

  // Load WebAuthn script from flow - matching LoginPage approach
  React.useEffect(() => {
    if (!flow) return;

    // Check if we have a WebAuthn script node and load it
    const webauthnNode = flow.ui.nodes.find((node: any) => 
      node.type === 'script' && (node.group === 'webauthn' || node.group === 'passkey')
    );

    if (webauthnNode && !document.getElementById('webauthn_script')) {
      console.log('Loading WebAuthn script from flow...', webauthnNode);
      const script = document.createElement('script');
      script.id = 'webauthn_script';
      const attrs = webauthnNode.attributes as any;
      script.src = attrs.src as string;
      script.async = attrs.async as boolean;
      if (attrs.crossorigin) {
        script.crossOrigin = attrs.crossorigin as string;
      }
      if (attrs.integrity) {
        script.integrity = attrs.integrity as string;
      }
      if (attrs.referrerpolicy) {
        script.referrerPolicy = attrs.referrerpolicy as string;
      }
      if (attrs.nonce) {
        script.nonce = attrs.nonce as string;
      }
      
      script.onload = () => {
        console.log('WebAuthn script loaded successfully from flow');
        console.log('Available window functions:', Object.keys(window).filter(key => key.includes('ory') || key.includes('Passkey')));
      };
      script.onerror = () => {
        console.error('Failed to load WebAuthn script from flow');
      };
      
      document.head.appendChild(script);
      console.log('WebAuthn script added to document head');
    }
  }, [flow]);

  // Debug DOM elements when WebAuthn is active
  React.useEffect(() => {
    if (registerWebAuthn && passkeyCreateData && webAuthnLink) {
      console.log('WebAuthn is active, checking DOM elements:');
      console.log('passkey_create_data element:', document.getElementById('passkey_create_data'));
      console.log('passkey_create_data by name:', document.getElementsByName('passkey_create_data')[0]);
      console.log('Form element:', document.getElementById('settings'));
      console.log('WebAuthn link:', webAuthnLink);
      
      const passkeyEl = document.getElementById('passkey_create_data');
      if (passkeyEl) {
        console.log('passkey_create_data value:', passkeyEl.getAttribute('value'));
        console.log('passkey_create_data form:', passkeyEl.closest('form'));
      }
    }
  }, [registerWebAuthn, passkeyCreateData, webAuthnLink]);

  // Handle flow response - matching original logic exactly
  React.useEffect(() => {
    if (!flow) return;

    console.log('Settings flow state:', flow);

    // Check if flow completed successfully
    if (flow.state === 'success') {
      navigate('/dashboard');
      return;
    }

    // Handle redirects for invalid flows - matching original
    if ([403, 404, 410].includes((flow as any).status)) {
      navigate('/auth/login');
      return;
    }

    // Extract flow data - matching original extractFlowData method
    const nodes = flow.ui.nodes;
    const identifierNode = nodes.find((n: any) => n.attributes.name === 'traits.email');
    const csrfNode = nodes.find((n: any) => n.attributes.name === 'csrf_token');
    const passkeyDataNode = nodes.find((n: any) => n.attributes.name === 'passkey_create_data');
    const webAuthnNode = nodes.find((n: any) => n.attributes.name === 'passkey_register_trigger');

    setIdentifier((identifierNode?.attributes as any)?.value ? String((identifierNode?.attributes as any).value) : '');
    setCsrfToken((csrfNode?.attributes as any)?.value ? String((csrfNode?.attributes as any).value) : '');
    setPasskeyCreateData((passkeyDataNode?.attributes as any)?.value ? String((passkeyDataNode?.attributes as any).value) : '');
    setWebAuthnLink((webAuthnNode?.attributes as any)?.onclick ? String((webAuthnNode?.attributes as any).onclick) : '');
  }, [flow, navigate]);

  // Method selection handlers - matching original exactly
  const passwordlessSelected = () => {
    setMethodSelector(false);
    setRegisterWebAuthn(true);
    setMethod('passkey');
  };

  const passwordSelected = () => {
    setMethodSelector(false);
    setRegisterPassword(true);
    setMethod('password');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex items-center justify-center py-8">
            <div className="text-sm text-muted-foreground">Loading...</div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error || !flow) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Error</CardTitle>
            <CardDescription>Failed to load settings form</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {error instanceof Error ? error.message : 'An unexpected error occurred'}
            </p>
            <Button onClick={() => window.location.href = '/auth/login'} className="w-full">
              Back to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Get UI content based on current state - matching original
  const getTitle = () => {
    if (webAuthnLink && registerWebAuthn) return 'Use your Key, Fingerprint or Face Recognition to create your Access Key';
    if (registerPassword) return 'Enter your new password';
    if (!webAuthnLink) return 'Setup your credentials';
    return 'Setup your credentials';
  };

  const getDescription = () => {
    if (webAuthnLink && registerWebAuthn) return `Configure your passwordless access for the user ${identifier}`;
    if (methodSelector) return 'Choose your access method';
    return '';
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-light">{getTitle()}</CardTitle>
          {getDescription() && (
            <CardDescription className="text-base">{getDescription()}</CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Render Kratos form - required for WebAuthn authentication */}
          {flow && (
            <form 
              id="kratos-form"
              action={flow.ui.action}
              method={flow.ui.method}
              style={{ display: 'none' }}
              onSubmit={(e) => {
                e.preventDefault();
                console.log('Kratos settings form submitted - handling via React');
                // Handle form submission via our React logic
                const formData = new FormData(e.currentTarget);
                const values: any = {};
                formData.forEach((value, key) => {
                  values[key] = value;
                });
                console.log('Settings form values:', values);
                
                // For settings, we might need to handle differently
                // This would typically update the user's settings
                window.location.href = '/dashboard'; // Redirect to dashboard after successful setup
              }}
            >
              {flow.ui.nodes
                .filter((node: any) => node.type === 'input')
                .map((node: any, index: number) => {
                  const attrs = node.attributes;
                  let value = String(attrs.value || '');
                  
                  // Update values with current React state
                  if (attrs.name === 'traits.email') {
                    value = identifier;
                  } else if (attrs.name === 'csrf_token') {
                    value = csrfToken;
                  } else if (attrs.name === 'passkey_create_data') {
                    value = passkeyCreateData;
                  } else if (attrs.name === 'method') {
                    value = method;
                  } else if (attrs.name === 'webauthn_register_displayname') {
                    value = securityKeyName;
                  }
                  
                  return (
                    <input
                      key={index}
                      type={attrs.type as string}
                      name={attrs.name as string}
                      value={value}
                      disabled={attrs.disabled as boolean}
                      required={attrs.required as boolean}
                      readOnly={true}
                      style={{ display: attrs.type === 'hidden' ? 'none' : 'block' }}
                    />
                  );
                })}
            </form>
          )}

          {/* User-facing form - matching original exactly */}
          <form 
            id="settings"
            action={flow.ui.action} 
            method="post" 
            encType="application/x-www-form-urlencoded"
            onSubmit={() => setIsSubmitting(true)}
          >
            {/* Hidden fields - matching original */}
            <input
              type="hidden"
              id="csrf_token"
              name="csrf_token"
              value={csrfToken}
              required
              readOnly
            />
            
            <input
              type="hidden"
              id="passkey_create_data"
              name="passkey_create_data"
              value={passkeyCreateData || ''}
              required
              readOnly
            />
            
            <input
              type="hidden"
              id="method"
              name="method"
              value={method}
            />
            
            <input
              type="hidden"
              name="traits.email"
              value={identifier}
              autoComplete="off"
            />

            {registerWebAuthn && (
              <input
                type="hidden"
                id="passkey_settings_register"
                name="passkey_settings_register"
                value=""
              />
            )}

            {/* Method Selector - matching original */}
            {methodSelector && (
              <div className="space-y-4">
                <Button 
                  type="button"
                  onClick={passwordlessSelected}
                  className="w-full h-12 text-base font-normal"
                  variant="default"
                >
                  <Key className="mr-2 h-4 w-4" />
                  Passwordless
                </Button>
                <Button 
                  type="button"
                  onClick={passwordSelected}
                  className="w-full h-12 text-base font-normal"
                  variant="outline"
                >
                  <Lock className="mr-2 h-4 w-4" />
                  Password
                </Button>
              </div>
            )}

            {/* Password Form - matching original */}
            {registerPassword && (
              <div className="space-y-4">
                <Input
                  type="password"
                  name="password"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="new-password"
                  required
                />
                <Button 
                  type="submit" 
                  className="w-full h-12 text-base font-normal" 
                  disabled={isSubmitting || !password}
                >
                  {isSubmitting ? 'Updating...' : 'Continue'}
                </Button>
              </div>
            )}

            {/* WebAuthn Form - matching original */}
            {webAuthnLink && registerWebAuthn && (
              <div className="space-y-4">
                <div className="text-center space-y-2">
                  <div className="text-sm text-muted-foreground">
                    Choose a name for your passwordless key and press the button below.
                  </div>
                </div>
                
                <Input
                  type="text"
                  name="webauthn_register_displayname"
                  placeholder="Name of the security key"
                  value={securityKeyName}
                  onChange={(e) => setSecurityKeyName(e.target.value)}
                  disabled={isSubmitting}
                  autoComplete="off"
                  required
                />

                {/* Use native button with onclick attribute - exactly like original Vue code */}
                <button
                  type="button"
                  name="passkey_register_trigger"
                  ref={(el) => {
                    if (el && webAuthnLink) {
                      // Set onclick attribute directly as string - matching original Vue :onclick
                      el.setAttribute('onclick', webAuthnLink);
                    }
                  }}
                  className="w-full h-12 text-base font-normal bg-primary text-primary-foreground hover:bg-primary/90 rounded-md disabled:opacity-50 disabled:pointer-events-none"
                  disabled={isSubmitting || !securityKeyName}
                >
                  {isSubmitting ? 'Setting up...' : 'Continue'}
                </button>
              </div>
            )}
          </form>

          {/* Link back to login */}
          <div className="text-center">
            <Link 
              to="/auth/login" 
              className="text-sm text-muted-foreground hover:text-primary"
            >
              Back to Login
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}